/**
 * webcodecs-audio-decode.js — decode AAC-in-MP4-family (m4a / m4b / mp4 / mov /
 * aac / caf) AND mp3 via the native WebCodecs AudioDecoder. Returns an
 * AudioBuffer, same contract as AudioContext.decodeAudioData.
 *
 * WHY (mp4-family): on iOS Safari / some Yandex builds, native decodeAudioData
 * fails to demux QuickTime .mov and certain mp4 containers ("Decoding failed"
 * — historically the single biggest decode-failure cohort in prod telemetry:
 * mov 153 / 4 d, all Safari+Yandex). MP4Box demuxes the AAC track and the
 * hardware AudioDecoder decodes it — NO 30 MB FFmpeg WASM, NO
 * SharedArrayBuffer/COEP (the repo bans those, so FFmpeg can only run its slow
 * single-thread core that times out on mobile).
 *
 * WHY (mp3 added 2026-06-19): `decodeAudioData` is whole-buffer — for a long
 * mp3 (~43 MB / 46 min) it allocates ~1 GB of Float32 PCM up front, and on
 * Yandex Browser the call stalls past the 30 s Tier 1 timeout (6 events / 30 d
 * on /volume alone, plus a user feedback "дрянь ваш инструмент" 2026-06-18).
 * Tier 2 FFmpeg WASM eventually rescues but takes 2–3 min on single-thread,
 * by which time the user has abandoned. WebCodecs `AudioDecoder({codec:'mp3'})`
 * is hardware-accelerated, streams one frame at a time, and decodes 43 MB in
 * ~3-8 s — closing the gap structurally rather than tuning timeouts.
 *
 * For mp3 we delegate to Mp3DecoderStream (existing OOM-proof streaming
 * decoder used by audio-stream-pipeline) and accumulate its per-frame
 * AudioData into a single AudioBuffer at the end. The mp4 path is unchanged.
 *
 * G.711 (`alaw` / `ulaw` sample entries — IP cameras, DVRs) in an mp4 / mov is
 * decoded here by table, with no codec at all (2026-09-29, see decodeG711);
 * other sound entries mp4box cannot type are refused BY NAME, not as «no audio
 * track».
 *
 * Both paths are proven RMS-equal to native on real fixtures (real iPhone
 * .mov, real ffmpeg AAC files, real Google Chrome) and are wired as a tier
 * BEFORE FFmpeg in AudioDecoderRobust, fully guarded — any failure falls
 * through to the FFmpeg/recover chain exactly as before, so the change can
 * only improve decode, never regress.
 *
 *   window.WebCodecsAudioDecode.supported(file) → bool (cheap, no I/O)
 *   window.WebCodecsAudioDecode.prefersFirst(file) → bool — if true,
 *       robust() should try this tier BEFORE native tier 1 (and skip tier 1
 *       on failure). Used for large mp3 where decodeAudioData stalls 30 s.
 *   window.WebCodecsAudioDecode.decode(file, ctx) → Promise<AudioBuffer>
 */
(function(){
    'use strict';
    if (window.WebCodecsAudioDecode) return;

    var MP4_RE = /\.(m4a|m4b|m4v|mp4|mov|aac|caf)$/i;
    var MP4_MIME_RE = /(mp4|m4a|aac|quicktime|x-m4a)/i;
    var MP3_RE = /\.mp3$/i;
    var MP3_MIME_RE = /audio\/(mpeg|mp3|x-mp3|mpeg3|x-mpeg3)/i;
    // Threshold above which `decodeAudioData` on mp3 is risky enough that we
    // skip it. 20 MB ≈ 21 min @ 128 kbps stereo; below this Tier 1 native is
    // genuinely fast (~80 ms) and worth trying first. Above this it routinely
    // stalls past our 30 s timeout (Yandex/Chromium long-mp3 cohort).
    var MP3_PREFER_FIRST_BYTES = 20 * 1024 * 1024;

    // AAC-LC AudioSpecificConfig (2 bytes) from sampleRate+channels. Needed for
    // QuickTime .mov where MP4Box reports codec 'mp4a' with no esds descriptor.
    function aacAsc(sampleRate, channels){
        var idx = [96000,88200,64000,48000,44100,32000,24000,22050,16000,12000,11025,8000,7350].indexOf(sampleRate);
        if (idx < 0) idx = 4;
        return new Uint8Array([ (2 << 3) | (idx >> 1), ((idx & 1) << 7) | ((channels || 1) << 3) ]);
    }

    var _mp4Load = null;
    function loadMp4Box(){
        if (window.MP4Box) return Promise.resolve();
        if (_mp4Load) return _mp4Load;
        _mp4Load = new Promise(function(res, rej){
            var s = document.createElement('script');
            s.src = '/vendor/mp4box/mp4box.all.min.js';
            s.onload = function(){ res(); };
            s.onerror = function(){
                // Same-origin proxy BEFORE the public CDN. The old code jumped
                // straight to cdn.jsdelivr.net and called that "the RU-edge
                // fallback" — but jsdelivr is exactly what an RU user may not
                // reach, so the fallback failed in the one case it existed for.
                // /cdn/jsdelivr/ is our nginx proxy (infra/nginx/ru-cdn-locations.conf),
                // served from our own host on both origins.
                var sources = ['/cdn/jsdelivr/npm/mp4box@0.5.4/dist/mp4box.all.min.js',
                               'https://cdn.jsdelivr.net/npm/mp4box@0.5.4/dist/mp4box.all.min.js'];
                (function next(i){
                    if (i >= sources.length) { rej(new Error('mp4box load failed')); return; }
                    var s2 = document.createElement('script');
                    s2.src = sources[i];
                    s2.onload = function(){ res(); };
                    s2.onerror = function(){ next(i + 1); };
                    document.head.appendChild(s2);
                })(0);
            };
            document.head.appendChild(s);
        });
        return _mp4Load;
    }

    function isMp3(file){
        if (!file) return false;
        var name = file.name || '';
        var type = file.type || '';
        return MP3_RE.test(name) || (!!type && MP3_MIME_RE.test(type));
    }

    function isMp4Family(file){
        if (!file) return false;
        var name = file.name || '';
        var type = file.type || '';
        return MP4_RE.test(name) || (!!type && MP4_MIME_RE.test(type));
    }

    function supported(file){
        if (typeof AudioDecoder === 'undefined' || typeof EncodedAudioChunk === 'undefined') return false;
        if (!file) return false;
        if (isMp4Family(file)) return true;
        // mp3 requires Mp3DecoderStream (already shipped, used by audio-stream-pipeline).
        // Runtime codec gate happens inside Mp3DecoderStream.decode (configure throws ↦
        // we fall through to FFmpeg). Safari has no AudioDecoder so we never get here.
        if (isMp3(file) && window.Mp3DecoderStream && window.Mp3FrameTrim) return true;
        return false;
    }

    // True when this tier should run BEFORE native tier 1 (AudioDecoderRobust
    // will skip the 30 s decodeAudioData attempt on success). Reserved for
    // formats where native is known to stall on size; small files still go
    // through native first because native is faster on those.
    function prefersFirst(file){
        if (!file) return false;
        if (!supported(file)) return false;
        var size = (file && file.size) || 0;
        if (isMp3(file) && size >= MP3_PREFER_FIRST_BYTES) return true;
        // mp4-family goes through WebCodecs FIRST (2026-09-02). Native
        // decodeAudioData is not a reliable oracle for AAC-in-MP4: measured on
        // one HE-AAC v2 TikTok file, Yandex Browser 26.8 returns a buffer of
        // TWICE the duration, an octave down and folded to mono, and on the
        // same stream with a core-rate header it throws "Unable to decode" —
        // while `<video>` plays it fine. Chrome 148/152 and Edge 152 decode it
        // correctly, so the class was invisible from Chrome. Prod, 30 d: tier 1
        // fails 2 199 Yandex sessions on top, most with that same message.
        // This tier reads the container's clock instead of any label (see
        // measureRate) and is ~equal in cost (decode-truth harness: 158–300 ms
        // vs 200–430 ms native on a 62 s clip), so it goes first; native stays
        // the fallback — see nativeAfterFailure.
        if (isMp4Family(file)) return true;
        return false;
    }

    // When the preferred-first tier fails, should robust() still try native
    // decodeAudioData? For big mp3 no — native is the thing known to stall on
    // size, retrying it only burns the 30 s budget again. For mp4-family YES:
    // a WebCodecs refusal there is usually a codec the platform decoder lacks
    // (AC-3, ALAC on Windows…), which native or FFmpeg may still decode.
    function nativeAfterFailure(file){
        return !!file && isMp4Family(file) && !isMp3(file);
    }

    function descFromEsds(mp4, id){
        try {
            var trak = mp4.getTrackById(id), entries = trak.mdia.minf.stbl.stsd.entries;
            for (var i = 0; i < entries.length; i++) {
                var e = entries[i];
                if (e.esds && e.esds.esd && e.esds.esd.descs && e.esds.esd.descs[0]) {
                    var dcd = e.esds.esd.descs[0];               // DecoderConfigDescriptor
                    if (dcd.descs && dcd.descs[0] && dcd.descs[0].data) return dcd.descs[0].data; // DecoderSpecificInfo
                }
            }
        } catch (_){}
        return null;
    }

    // mp3 path: delegate to Mp3DecoderStream (which already does ID3 skip,
    // Xing/Info skip, frame indexing, AudioDecoder.configure with s16→f32
    // conversion guard) and concatenate its onChunk samples into one
    // AudioBuffer at the end. Plane assembly mirrors the mp4-family done() —
    // single ctx.createBuffer allocation + per-channel set() copies.
    async function decodeMp3(file, ctx){
        if (!window.Mp3DecoderStream || typeof window.Mp3DecoderStream.decode !== 'function') {
            throw new Error('Mp3DecoderStream unavailable');
        }
        var planes = [];
        var sampleRate = 0;
        var channels = 0;
        var totalFrames = 0;
        var firstChunkErr = null;

        await window.Mp3DecoderStream.decode(file, {
            onChunk: function(sample){
                if (firstChunkErr) return;
                try {
                    if (!sampleRate) sampleRate = sample.sampleRate;
                    if (!channels) {
                        channels = sample.channelData.length;
                        for (var c = 0; c < channels; c++) planes.push([]);
                    }
                    // Channel count must stay stable across frames — bail loud
                    // if a mid-stream mode change shows up (rare but possible
                    // in concatenated mp3s). Caller falls through to FFmpeg.
                    var nch = Math.min(channels, sample.channelData.length);
                    for (var c2 = 0; c2 < nch; c2++) {
                        if (sample.channelData[c2]) planes[c2].push(sample.channelData[c2]);
                    }
                    totalFrames += sample.numberOfFrames;
                } catch (e) {
                    firstChunkErr = e;
                }
            }
        });

        if (firstChunkErr) throw firstChunkErr;
        if (!totalFrames || !sampleRate || !channels) {
            throw new Error('mp3 webcodecs decoded 0 frames');
        }
        var ab = ctx.createBuffer(channels, totalFrames, sampleRate);
        for (var c = 0; c < channels; c++) {
            var dst = ab.getChannelData(c);
            var off = 0;
            var segs = planes[c];
            for (var i = 0; i < segs.length; i++) {
                dst.set(segs[i], off);
                off += segs[i].length;
            }
        }
        return ab;
    }

    // ── Sample-rate table + the one measurement that survives every browser ──
    //
    // AudioData.sampleRate is a LABEL, and it lies. Measured 2026-09-02 on the
    // same HE-AAC v2 TikTok file (mp4a.40.29, header 44100): Chrome 148/152 and
    // Edge 152 label the output 44100 (true), Yandex Browser 26.8 labels it 88200
    // (config × 2). The header rate lies the other way: for SBR streams muxed
    // by GPAC/Nero/most phones the sample entry carries the CORE rate (16000 →
    // decoder emits 32000). Building the AudioBuffer from either label gave the
    // "video is slowed down and the voice is an octave lower" class (support
    // 2026-09-01, /video-watermark → /video-crop → /reels-maker, one file).
    //
    // What never lies is the container's own clock: each AAC access unit has a
    // duration in the track timescale (the same clock the video is synced to),
    // and the decoder tells us how many PCM frames it produced for it.
    //   rate = frames-per-chunk / chunk-duration-in-seconds
    // 2048 frames over 46.44 ms → 44100; 2048 over 64 ms → 32000. Snapped to
    // the AAC rate table when within 3 % (the table's neighbours differ by ≥9 %).
    var AAC_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];
    function median(arr){
        if (!arr.length) return 0;
        var a = arr.slice().sort(function(x, y){ return x - y; });
        return a[a.length >> 1];
    }
    function snapRate(r){
        if (!(r > 0)) return 0;
        for (var i = 0; i < AAC_RATES.length; i++) {
            if (Math.abs(AAC_RATES[i] - r) / AAC_RATES[i] <= 0.03) return AAC_RATES[i];
        }
        return Math.round(r);
    }
    // The CORE sampling rate from an AudioSpecificConfig (ISO 14496-3 §1.6.2.1):
    // audioObjectType(5, escape 31→+6) samplingFrequencyIndex(4, 15→24-bit
    // explicit) channelConfiguration(4). For every object type — LC, HE (5),
    // HE v2 (29), backward-compatible LC+sync-extension, or purely implicit
    // SBR — that first index is the rate of the AAC core; the SBR output rate
    // (2×) is signalled elsewhere or not at all. It is the one number the
    // decoder must be configured with:
    //   Yandex Browser 26.8 treats config.sampleRate AS the core rate. Fed the
    //   sample-entry rate of a TikTok HE-AAC v2 (44100 = the SBR output), it
    //   builds the SBR band tables for a 44.1 kHz core → the high band lands on
    //   the wrong frequencies (spectral centroid 3754 vs 2571, RMS ×1.9 —
    //   "сильно искажает", support 2026-09-01) and labels the output 88200.
    //   Fed 22050, it decodes bit-identically to Chrome. Chrome/Edge take the
    //   rate from the description and ignore the config value either way.
    // Returns 0 when the bytes cannot be read (caller falls back to the header).
    var ASC_RATES = AAC_RATES;
    function ascCoreRate(bytes){
        try {
            if (!bytes || bytes.length < 2) return 0;
            var pos = 0;
            function bits(n){ var v = 0; for (var i = 0; i < n; i++) { var b = bytes[pos >> 3]; if (b === undefined) throw new Error('short'); v = (v << 1) | ((b >> (7 - (pos & 7))) & 1); pos++; } return v >>> 0; }
            var aot = bits(5); if (aot === 31) aot = 32 + bits(6);
            var sfi = bits(4);
            if (sfi === 15) return bits(24);
            return ASC_RATES[sfi] || 0;
        } catch (_) { return 0; }
    }

    // Pure: what the decoder's output + the container's clock say the rate is.
    // Exposed for the node guard (tests/node/webcodecs-audio-rate.test.mjs).
    function measureRate(framesPerChunk, chunkSeconds, labelRate){
        var f = median(framesPerChunk), s = median(chunkSeconds);
        if (f > 0 && s > 0) return snapRate(f / s);
        return labelRate > 0 ? labelRate : 0;
    }

    // ── What the decoder REALLY emits, before a relay commits to a format ──
    //
    // A relay (decode → re-encode unit by unit: webm-encoder.js, the
    // pipeline's muxAudioLazy) fixes its encoder and its muxer track before
    // the stream runs, and neither number it had is the stream's. Measured
    // 2026-10-08 on the four HE-AAC v2 fixtures (tests/fixtures/heaac):
    //   Chrome 154 / Edge 154 emit MONO (no parametric stereo) at 32000 for a
    //   sample entry saying 2 channels at 16000 or 32000; Yandex 26 emits
    //   stereo, labelled 64000 when configured with the header's 32000 and
    //   32000 when configured with the ASC core rate (16000).
    // An encoder configured from the header threw «Input audio buffer is
    // incompatible with codec parameters» on the first unit, and the next
    // unit's «Cannot call 'encode' on a closed codec» hid it (reproduced in
    // Chrome and Yandex) — the pipeline's top audio failure in the field
    // (30 days: 59 «closed codec», 21 «incompatible», 7 «flush on closed»,
    // Yandex desktop above all), and every HE-AAC v2 → WebM.
    //   base   the caller's AudioDecoder config (codec, rate, channels, description)
    //   units  [{timestamp µs, duration µs, data?}], bytesOf(unit) → Uint8Array
    // Tries the configs the stream may be decoded with (AAC: the ASC core
    // rate first — see ascCoreRate), each by deed: the first units decoded,
    // the rate read off the container's clock. → { config, sampleRate,
    // channels, labelRate } or null (nothing here decodes it).
    var RELAY_PROBE_UNITS = 10, RELAY_PROBE_MS = 15000;
    async function relayFormat(base, units, bytesOf){
        if (!base || !base.codec || !units || !units.length || typeof AudioDecoder === 'undefined' || typeof EncodedAudioChunk === 'undefined') return null;
        var cands = [], aac = /^mp4a/i.test(base.codec);
        if (aac && base.description && base.description.length) {
            var core = ascCoreRate(base.description);
            if (core > 0 && core !== base.sampleRate) cands.push(Object.assign({}, base, { sampleRate: core }));
        }
        cands.push(base);
        var n = Math.min(units.length, RELAY_PROBE_UNITS), chunkSeconds = [], data = [];
        try {
            for (var i = 0; i < n; i++) {
                var u = units[i];
                var cs = (u.duration || 0) / 1e6;
                if (cs > 0.005 && cs < 0.5) chunkSeconds.push(cs);
                data.push(u.data || await bytesOf(u));
            }
        } catch (_) { return null; }
        for (var c = 0; c < cands.length; c++) {
            var cfg = cands[c];
            try { var sup = await AudioDecoder.isConfigSupported(cfg); if (!sup || !sup.supported) continue; } catch (_) { continue; }
            var outs = await probeOutputs(cfg, units, data);
            if (!outs || !outs.length) continue;
            var frames = [], counts = {}, best = 0, ch = 0;
            for (var k = 0; k < outs.length; k++) {
                frames.push(outs[k].frames);
                counts[outs[k].ch] = (counts[outs[k].ch] || 0) + 1;
                if (counts[outs[k].ch] > best) { best = counts[outs[k].ch]; ch = outs[k].ch; }
            }
            var rate = measureRate(frames, chunkSeconds, outs[0].label);
            if (!(rate > 0) || !(ch > 0)) continue;
            return { config: cfg, sampleRate: rate, channels: ch, labelRate: outs[0].label };
        }
        return null;
    }
    function probeOutputs(cfg, units, data){
        return new Promise(function(resolve){
            var outs = [], dec = null, done = false;
            var timer = setTimeout(function(){ finish(null); }, RELAY_PROBE_MS);
            function finish(v){ if (done) return; done = true; clearTimeout(timer); try { if (dec && dec.state !== 'closed') dec.close(); } catch (_) {} resolve(v); }
            try {
                dec = new AudioDecoder({
                    output: function(d){ outs.push({ frames: d.numberOfFrames, ch: d.numberOfChannels, label: d.sampleRate }); d.close(); },
                    error: function(){ finish(null); }
                });
                dec.configure(cfg);
                for (var i = 0; i < data.length; i++) {
                    dec.decode(new EncodedAudioChunk({ type: 'key', timestamp: units[i].timestamp || 0, duration: units[i].duration || 0, data: data[i] }));
                }
                dec.flush().then(function(){ finish(outs); }, function(){ finish(null); });
            } catch (_) { finish(null); }
        });
    }

    // One decoded unit in the format a relay's track was planned for: the
    // label it SHOULD carry (relayFormat's measured rate — the samples are not
    // touched, only the number beside them) and the channel count of the
    // track (mono duplicated, more averaged to mono or cut to the first two).
    // Returns `d` itself when it already fits; otherwise a new f32-planar
    // AudioData — the caller closes both.
    function conformAudioData(d, sampleRate, channels){
        var nch = d.numberOfChannels, nf = d.numberOfFrames;
        if (d.sampleRate === sampleRate && nch === channels) return d;
        var planes = [];
        for (var c = 0; c < nch; c++) {
            var p = new Float32Array(nf);
            d.copyTo(p, { planeIndex: c, format: 'f32-planar' });
            planes.push(p);
        }
        var out = new Float32Array(nf * channels);
        for (var oc = 0; oc < channels; oc++) {
            var dst = out.subarray(oc * nf, (oc + 1) * nf);
            if (channels === 1 && nch > 1) {
                for (var j = 0; j < nf; j++) { var s = 0; for (var q = 0; q < nch; q++) s += planes[q][j]; dst[j] = s / nch; }
            } else {
                dst.set(planes[Math.min(oc, nch - 1)]);
            }
        }
        return new AudioData({ format: 'f32-planar', sampleRate: sampleRate, numberOfFrames: nf,
            numberOfChannels: channels, timestamp: d.timestamp, data: out });
    }

    /* ── The edit list, one reading for every path of decode() ────────────────
       The head cut (encoder priming, a late-joining track) and the kept span,
       in seconds of MEDIA time — what native decodeAudioData honours. */
    function editWindow(mp4, at){
        var skipSec = 0, keepSec = 0;
        try {
            var trak = mp4.getTrackById(at.id);
            /* ⚠️ Список правок mp4box отдаёт ДВАЖДЫ и по-разному:
               в описании дорожки это ГОТОВЫЙ МАССИВ `at.edits`
               (`i.edts && (r.edits = i.edts.elst.entries)` в
               mp4box.all.min.js), а у самого бокса — `trak.edts
               .elst.entries`. Прежний код читал `trak.edits`,
               которого нет ни там, ни там, поэтому запасной путь
               НЕ СРАБАТЫВАЛ НИ РАЗУ, а читатель Mp4EditList
               подключён только там, где загружен webcodecs-
               pipeline. Итог (замер 16.09.2026 на настоящей
               съёмке): на /video-volume и /add-music-to-video
               ярус 1.5 отдавал МЕДИА-дорожку целиком —
               8 791 040 сэмплов против 8 790 000 у штатного
               decodeAudioData, — то есть звук в перекодированной
               выдаче уезжал на 1024 сэмпла (21.3 мс) позже
               картинки, и так на КАЖДОМ файле с праймингом AAC
               (это почти всякий mp4 с телефона). */
            var ents = (at && at.edits && at.edits.length) ? at.edits
                : (trak && trak.edts && trak.edts.elst && trak.edts.elst.entries);
            var mvTs = mp4.moov && mp4.moov.mvhd && mp4.moov.mvhd.timescale;
            // The edit-list reader when a lane has loaded it
            // (webcodecs-pipeline does before any audio decode):
            // it also understands «empty edit + media edit», the
            // shape of a track that joins late. The buffer this
            // module returns is the track's MEDIA from the cut —
            // never padded by the lead; the caller pads (see
            // muxSpeedAudio `leadSec`). Without the reader the
            // single-edit reading below stays as it was.
            var ML = window.Mp4EditList;
            var pl = (ML && typeof ML.plan === 'function') ? ML.plan(at, mvTs) : null;
            if (pl && (pl.shape === 'media' || pl.shape === 'empty+media')) {
                skipSec = pl.mediaStartUs / 1e6;
                if (pl.presDurUs > 0) keepSec = pl.presDurUs / 1e6;
            } else if (ents && ents.length === 1 && ents[0].media_time > 0 && at.timescale > 0) {
                skipSec = ents[0].media_time / at.timescale;
                if (ents[0].segment_duration > 0 && mvTs > 0) keepSec = ents[0].segment_duration / mvTs;
            }
        } catch (_) {}
        return { skipSec: skipSec, keepSec: keepSec };
    }

    /* Frames to drop from the head and to keep, from the edit window and the
       container's own length (the decoder pads the last unit to a full frame). */
    function frameWindow(totalFrames, rate, skipSec, keepSec, trackDurSec){
        var skip = skipSec > 0 ? Math.round(skipSec * rate) : 0;
        if (skip >= totalFrames) skip = 0;
        var keep = totalFrames - skip;
        var boundSec = keepSec > 0 ? keepSec : trackDurSec;
        if (boundSec > 0) {
            var bound = Math.round(boundSec * rate);
            if (bound > 0 && bound < keep) keep = bound;
        }
        return { skip: skip, keep: keep };
    }

    /* ── G.711 in an ISO / QuickTime box: decoded HERE (29.09.2026) ───────────
       IP cameras and DVRs write their sound as G.711 — `alaw` / `ulaw` sample
       entries in an mp4 or mov. mp4box 0.5.4 does not type those entries as
       audio (only mp4a, Opus, ac-3, ec-3, fLaC, mha1, mhm1, enca), so this tier
       answered «no audio track» about a file that has one; native
       decodeAudioData refuses it on WebKit («Decoding failed»); and every
       robust() of such a file went down to the 32 MB FFmpeg core for what is a
       256-entry table lookup. Prod, 24–25.09: one paying iPhone customer's
       security-camera clip (H.264 + pcm_alaw 16 kHz mono) took that ladder in
       six tools — /volume, /equalizer, /audio-restoration, /repair-audio,
       /video-volume, /voice-enhancer — two logged tool errors per load.
       G.711 is one byte per sample per channel, interleaved; the tables are
       ITU-T G.711 as FFmpeg's pcm_alaw / pcm_mulaw expand them (bit-exact —
       tests/js/webcodecs-audio-decode-g711.test.mjs compares against the FFmpeg
       we ship). Other sound entries mp4box cannot type (QuickTime PCM `sowt` /
       `twos` / `lpcm`, `samr`, `alac` …) are refused BY NAME so the next tier
       takes them — «no audio track» about them was never true. */
    var G711 = null;
    function alawToS16(v){
        v ^= 0x55;
        var t = v & 0x0f, seg = (v & 0x70) >> 4;
        t = seg ? (t + t + 1 + 32) << (seg + 2) : (t + t + 1) << 3;
        return (v & 0x80) ? t : -t;
    }
    function ulawToS16(v){
        v = ~v & 0xff;
        var t = ((v & 0x0f) << 3) + 0x84;
        t <<= (v & 0x70) >> 4;
        return (v & 0x80) ? (0x84 - t) : (t - 0x84);
    }
    function g711Table(type){
        if (!G711) {
            var a = new Float32Array(256), u = new Float32Array(256);
            for (var i = 0; i < 256; i++) { a[i] = alawToS16(i) / 32768; u[i] = ulawToS16(i) / 32768; }
            G711 = { alaw: a, ulaw: u };
        }
        return G711[type] || null;
    }
    // The first sound track (handler 'soun') mp4box did not type as audio.
    function unreadSoundTrack(mp4, info){
        var tracks = (info && info.tracks) || [];
        for (var i = 0; i < tracks.length; i++) {
            var t = tracks[i];
            if (!t || t.type === 'audio' || t.type === 'video') continue;
            try {
                var trak = mp4.getTrackById(t.id);
                if (!trak || !trak.mdia || !trak.mdia.hdlr || trak.mdia.hdlr.handler !== 'soun') continue;
                var entry = trak.mdia.minf.stbl.stsd.entries[0];
                return { track: t, trak: trak, entry: entry, type: String((entry && entry.type) || t.codec || '') };
            } catch (_) {}
        }
        return null;
    }
    /* Channels and rate from the sound sample entry itself (QuickTime
       SoundDescription v0/v1, or v2 with a float64 rate); the media timescale
       when the entry states no usable rate. */
    function soundEntryFormat(buf, entry, timescale){
        if (!entry || !(entry.start >= 0) || entry.start + 36 > buf.byteLength) return null;
        var dv = new DataView(buf, entry.start, Math.min(72, buf.byteLength - entry.start));
        var ver = dv.getUint16(16), ch = 0, rate = 0;
        if (ver === 2 && dv.byteLength >= 52) { rate = Math.round(dv.getFloat64(40)); ch = dv.getUint32(48); }
        else { ch = dv.getUint16(24); rate = dv.getUint32(32) >>> 16; }
        if (!(rate >= 4000 && rate <= 192000)) rate = (timescale >= 4000 && timescale <= 192000) ? timescale : 0;
        if (!(ch >= 1 && ch <= 8) || !rate) return null;
        return { channels: ch, rate: rate };
    }
    function decodeG711(buf, mp4, snd, ctx){
        var table = g711Table(snd.type);
        if (!table) throw new Error('no decoder for sound entry ' + snd.type);
        var fmt = soundEntryFormat(buf, snd.entry, snd.track.timescale);
        if (!fmt) throw new Error('g711: unreadable sample entry');
        var si = mp4.getTrackSamplesInfo(snd.track.id);
        if (!si || !si.length) throw new Error('g711: no samples');
        // The track's bytes in decode order, contiguous runs copied at once
        // (QuickTime writes one "sample" per byte — tens of thousands a second).
        var total = 0, i;
        for (i = 0; i < si.length; i++) total += si[i].size;
        var src = new Uint8Array(buf), bytes = new Uint8Array(total), at = 0;
        var runAt = si[0].offset, runLen = 0;
        function flush(){
            if (runAt + runLen > src.length) throw new Error('g711: sample data past the end of the file');
            bytes.set(src.subarray(runAt, runAt + runLen), at); at += runLen;
        }
        for (i = 0; i < si.length; i++) {
            var s = si[i];
            if (s.offset === runAt + runLen) { runLen += s.size; continue; }
            flush(); runAt = s.offset; runLen = s.size;
        }
        flush();
        var ch = fmt.channels, frames = Math.floor(total / ch);
        if (!frames) throw new Error('g711: empty track');
        // The container's clock is the witness: one byte per sample per channel
        // at the entry's rate must fill the track's duration.
        var trackDurSec = (snd.track.duration > 0 && snd.track.timescale > 0) ? snd.track.duration / snd.track.timescale : 0;
        if (trackDurSec > 0) {
            var ratio = frames / fmt.rate / trackDurSec;
            if (ratio < 0.8 || ratio > 1.2) throw new Error('g711: length disagrees with the container (' + ratio.toFixed(2) + ')');
        }
        var ew = editWindow(mp4, snd.track);
        var win = frameWindow(frames, fmt.rate, ew.skipSec, ew.keepSec, trackDurSec);
        var ab = ctx.createBuffer(ch, win.keep, fmt.rate);
        for (var c = 0; c < ch; c++) {
            var dst = ab.getChannelData(c);
            for (var f = 0, p = win.skip * ch + c; f < win.keep; f++, p += ch) dst[f] = table[bytes[p]];
        }
        // How many camera files reach the audio tools — no other event names the codec.
        try { window.__ae && window.__ae('feature_use', {
            f: 'wc_g711_decoded', entry: snd.type, sr: fmt.rate, ch: ch, sec: Math.round(win.keep / fmt.rate)
        }); } catch (_) {}
        return ab;
    }

    function decode(file, ctx){
        if (typeof AudioDecoder === 'undefined') return Promise.reject(new Error('no AudioDecoder'));
        if (!ctx) return Promise.reject(new Error('no AudioContext'));
        if (isMp3(file)) return decodeMp3(file, ctx);
        return loadMp4Box().then(function(){
            return file.arrayBuffer();
        }).then(function(buf){
            return new Promise(function(resolve, reject){
                var mp4 = MP4Box.createFile();
                var dec = null, hdrRate = 0, hdrChannels = 0, totalFrames = 0, decErr = null, planes = [], settled = false;
                // Decoder-output truth (see the header of measureRate above).
                var outChannels = 0, labelRate = 0, framesPerChunk = [], chunkSeconds = [];
                /* What the failure looked like, for the field: the config the
                   decoder was given, how many access units it had swallowed and
                   whether it ever produced a frame. Android's «Decoding error.»
                   (2 014 events / 30 d, 13.09.2026) could not be told apart from
                   a bad description, a rate mismatch or a mid-stream refusal. */
                var cfgUsed = null, fedCount = 0, outCount = 0;
                // A sound track mp4box did not type as audio: its sample entry
                // (`alaw`, `sowt`, `samr` …) — named in the field report.
                var soundEntry = '';
                function annotate(e) {
                    try { if (e && typeof e === 'object' && !e.wcDetail) e.wcDetail = {
                        stage: cfgUsed ? (outCount ? 'stream' : 'first') : (soundEntry ? 'entry' : 'configure'),
                        codec: cfgUsed ? cfgUsed.codec : soundEntry, sr: cfgUsed ? cfgUsed.sampleRate : 0, hdr: hdrRate, ch: hdrChannels,
                        desc: (cfgUsed && cfgUsed.description) ? cfgUsed.description.length : 0,
                        fed: fedCount, out: outCount, name: (e && e.name) || '' }; } catch (_) {}
                    return e;
                }
                var readyFired = false;   // see the verdict after mp4.flush() below
                var trackDurSec = 0, editSkipSec = 0, editKeepSec = 0;
                // STALL watchdog, not a total-time limit (2026-07-08, comment
                // #938): the previous one-shot 60 s timer killed decodes of
                // long files (90-min audiobook m4a needs 60-90 s on a 4-core
                // box) even though frames were flowing the whole time. The
                // watchdog's job is to catch the Yandex/Chromium zombie-stall
                // class (decoder produces NOTHING and never rejects) — so it
                // must fire on 60 s of NO PROGRESS, and re-arm every time a
                // decoded frame arrives. A genuinely stalled decoder still
                // dies in 60 s; a slow-but-alive decode of any length now
                // completes. window.__wcStallMs overrides for tests.
                var WATCHDOG_MS = (typeof window !== 'undefined' && (window.__wcStallMs | 0)) || 60000;
                var watchdog = null;
                function armWatchdog(){
                    if (settled) return;
                    clearTimeout(watchdog);
                    watchdog = setTimeout(function(){ fail(new Error('webcodecs decode timeout')); }, WATCHDOG_MS);
                }
                armWatchdog();

                function fail(e){ if (settled) return; settled = true; clearTimeout(watchdog); try { if (dec && dec.state !== 'closed') dec.close(); } catch(_){} reject(annotate(e.message ? e : new Error('webcodecs decode error'))); }
                function done(){
                    if (settled) return; settled = true; clearTimeout(watchdog);
                    if (decErr) { reject(annotate(decErr.message ? decErr : new Error('webcodecs decode error'))); return; }
                    if (!totalFrames) { reject(annotate(new Error('webcodecs decoded 0 frames'))); return; }
                    var rate = measureRate(framesPerChunk, chunkSeconds, labelRate);
                    if (!(rate > 0)) { reject(new Error('webcodecs decode: no sample rate')); return; }
                    var channels = outChannels || hdrChannels || 1;
                    // The container's clock also bounds the LENGTH: the decoder pads
                    // the last access unit to a full frame (measured +134..180 ms on
                    // TikTok files), and an edit list may cut encoder priming off
                    // the head. Both are what native decodeAudioData honours.
                    var fw = frameWindow(totalFrames, rate, editSkipSec, editKeepSec, trackDurSec);
                    var skip = fw.skip, keep = fw.keep;
                    var ab = ctx.createBuffer(channels, keep, rate);
                    for (var ch = 0; ch < channels; ch++) {
                        var dst = ab.getChannelData(ch), off = -skip, segs = planes[ch] || [];
                        for (var i = 0; i < segs.length; i++) {
                            var seg = segs[i];
                            if (off + seg.length > 0 && off < keep) {
                                var from = off < 0 ? -off : 0;
                                var to = Math.min(seg.length, keep - off);
                                if (to > from) dst.set(seg.subarray(from, to), off + from);
                            }
                            off += seg.length;
                        }
                    }
                    if (rate !== hdrRate || channels !== hdrChannels || (labelRate && labelRate !== rate)) {
                        try { window.__ae && window.__ae('feature_use', {
                            f: 'wc_audio_rate_relabel', hdr: hdrRate, label: labelRate, measured: rate,
                            ch_hdr: hdrChannels, ch_out: channels
                        }); } catch (_) {}
                    }
                    resolve(ab);
                }

                mp4.onError = function(e){ fail(new Error('mp4box: ' + e)); };
                mp4.onSamples = function(id, user, samples){
                    for (var i = 0; i < samples.length; i++) {
                        var s = samples[i];
                        if (!dec || decErr) break;
                        // Only sane durations feed the rate measurement — GPAC
                        // writes a 1-unit first sample on a 28 224 000 timescale.
                        if (s.duration > 0 && s.timescale > 0 && chunkSeconds.length < 256) {
                            var cs = s.duration / s.timescale;
                            if (cs > 0.005 && cs < 0.5) chunkSeconds.push(cs);
                        }
                        try {
                            fedCount++;
                            dec.decode(new EncodedAudioChunk({
                                type: s.is_sync ? 'key' : 'delta',
                                timestamp: Math.round(s.cts / s.timescale * 1e6),
                                duration: Math.round(s.duration / s.timescale * 1e6),
                                data: s.data
                            }));
                        } catch (e){ decErr = e; }
                    }
                };
                mp4.onReady = function(info){
                    readyFired = true;
                    (function(){
                        var at = info.audioTracks && info.audioTracks[0];
                        if (!at) {
                            // A sound track mp4box could not type: G.711 is decoded
                            // right here; anything else is refused by name.
                            var snd = unreadSoundTrack(mp4, info);
                            if (!snd) { fail(new Error('no audio track')); return; }
                            soundEntry = snd.type.slice(0, 12);
                            if (!g711Table(snd.type)) { fail(new Error('no decoder for sound entry ' + snd.type)); return; }
                            var g;
                            try { g = decodeG711(buf, mp4, snd, ctx); } catch (eG) { fail(eG); return; }
                            settled = true; clearTimeout(watchdog);
                            resolve(g);
                            return;
                        }
                        hdrRate = at.audio.sample_rate; hdrChannels = at.audio.channel_count;
                        if (at.duration > 0 && at.timescale > 0) trackDurSec = at.duration / at.timescale;
                        var ew = editWindow(mp4, at);
                        editSkipSec = ew.skipSec; editKeepSec = ew.keepSec;
                        var codec = at.codec; if (codec.indexOf('.') < 0 && codec.indexOf('mp4a') === 0) codec = 'mp4a.40.2';
                        var realAsc = descFromEsds(mp4, at.id);
                        var desc = realAsc || aacAsc(hdrRate, hdrChannels);
                        // Configure with the CORE rate the ASC states, never the
                        // sample entry's — see ascCoreRate. Same description
                        // bytes; only the informative rate changes.
                        var coreRate = (realAsc && ascCoreRate(realAsc)) || hdrRate;
                        var cfg = { codec: codec, sampleRate: coreRate, numberOfChannels: hdrChannels, description: desc };
                        AudioDecoder.isConfigSupported(cfg).then(function(sup){
                            if (sup && sup.supported) return cfg;
                            var cfg2 = { codec: codec, sampleRate: hdrRate, numberOfChannels: hdrChannels, description: desc };
                            return AudioDecoder.isConfigSupported(cfg2).then(function(s2){
                                if (s2 && s2.supported) return cfg2;
                                var cfg3 = { codec: codec, sampleRate: hdrRate, numberOfChannels: hdrChannels };
                                return AudioDecoder.isConfigSupported(cfg3).then(function(s3){
                                    if (s3 && s3.supported) return cfg3;
                                    throw new Error('config unsupported: ' + codec);
                                });
                            });
                        }).then(function(useCfg){
                            dec = new AudioDecoder({
                                output: function(adt){
                                    armWatchdog();   // progress → decoder is alive, push the stall deadline out
                                    outCount++;
                                    var nf = adt.numberOfFrames, nch = adt.numberOfChannels;
                                    if (!outChannels) {
                                        // The DECODER's channel count, not the header's: SBR+PS
                                        // turns a mono core into stereo, and the header may say
                                        // either. Planes are allocated on the first output.
                                        outChannels = nch; labelRate = adt.sampleRate;
                                        for (var c = 0; c < nch; c++) planes[c] = [];
                                    } else if (nch !== outChannels) {
                                        decErr = new Error('webcodecs decode: channel count changed mid-stream');
                                        adt.close(); return;
                                    }
                                    if (framesPerChunk.length < 256) framesPerChunk.push(nf);
                                    for (var ch = 0; ch < nch; ch++) {
                                        var out = new Float32Array(nf);
                                        try { adt.copyTo(out, { planeIndex: ch, format: 'f32-planar' }); } catch (e){ decErr = e; }
                                        planes[ch].push(out);
                                    }
                                    totalFrames += nf; adt.close();
                                },
                                error: function(e){ decErr = e; }
                            });
                            cfgUsed = useCfg;
                            dec.configure(useCfg);
                            mp4.setExtractionOptions(at.id, null, { nbSamples: 1000000 });
                            mp4.start();
                            // All buffered samples are queued synchronously on start();
                            // flush() drains the decoder, then assemble.
                            dec.flush().then(done).catch(fail);
                        }).catch(fail);
                    })();
                };
                buf.fileStart = 0;
                mp4.appendBuffer(buf);
                mp4.flush();
                /* ⚠️ A VERDICT, not a timeout. The whole file has just been handed
                   to MP4Box in one synchronous append, and MP4Box calls onReady
                   from inside that append the moment it has parsed the track
                   header. If it has not fired by now, there is no ISO-BMFF track
                   header in these bytes — a raw ADTS «.aac», an Apple CAF, a
                   renamed mp3 — and nothing will ever arrive: MP4Box neither
                   resolves nor rejects on bytes it cannot frame. Until 15.09.2026
                   this path waited for the 60 s stall watchdog, and every such
                   file stood on «loading» for a minute before native decode
                   (which takes ~2 s) was even tried: 452 `.aac` timeouts in 359
                   sessions across 38 tools on RU in 30 days; report #1657
                   («с AAC не работает, висит»), reproduced in real Opera 135 at
                   66.8 s. Fail now and let the next tier have the file. */
                if (!readyFired && !settled) fail(new Error('webcodecs: no mp4 track header in this file'));
            });
        });
    }

    /* ── decodeRange: a WINDOW of the track, read lazily ─────────────────────
     *
     * decode() above hands MP4Box the whole file: `file.arrayBuffer()` (one
     * copy), MP4Box's own sample store (a second) and then the PCM of the
     * entire track. That is the right shape for an m4a somebody wants whole and
     * the wrong one for a video clip somebody wants twenty seconds of.
     * /slideshow-maker fed a 12-clip / 639 MB deck through exactly that, clip
     * by clip, and the renderer was OOM-killed two seconds after «Export» —
     * seven times in a row on a Galaxy S23 with 8 GB (support, 22.09.2026).
     * /video-merge had met the same wall a fortnight earlier (1.3 GB clip →
     * 2.6 GB in flight, «Array buffer allocation failed») and answered it with
     * FFmpeg windows; this is the WebCodecs answer, so the wasm is not needed
     * for the commonest file on the site — AAC in an mp4/mov from a phone.
     *
     * What is read: the top-level box HEADERS (16 bytes each), ftyp+moov (KB),
     * and only the access units inside [from, to) plus a couple for the
     * decoder's warm-up — sliced off the disk in runs of contiguous units, so
     * the video bytes interleaved between them are never touched. Heap is
     * O(window), whatever the file size.
     *
     * What comes back: an AudioBuffer holding EXACTLY [from, to) seconds of
     * PRESENTATION time (the edit list is honoured the way decode() honours
     * it: media time = presentation time + the list's head cut), at the rate
     * measured from the decoder's output against the container's clock — the
     * same truth decode() uses, because the label lies on Yandex. Past the end
     * of the track the buffer is silence, so a caller may ask for a slot
     * longer than the clip and get the clip followed by nothing.
     *
     *   WebCodecsAudioDecode.decodeRange(file, ctx, { from, to }) → Promise<AudioBuffer>
     *     from  seconds, ≥ 0 (default 0)
     *     to    seconds, > from; Infinity / absent = to the end of the track
     *   The buffer carries `_range = { from, to, units, reads, bytes, ms }`.
     *
     * STREAMING (30.09.2026): with `sink` nothing is kept — each decoded frame
     * goes to sink(planes, sampleRate) already cut to [from, to), and the
     * promise resolves to `{ rate, channels, units, reads, bytes, through, ms }`
     * instead of a buffer (ctx may be null). For a consumer that needs a
     * product of the sound, not the sound: AudioPeaksStream's waveform. Its
     * earlier demuxer stream kept every slice it read — the whole VIDEO,
     * because mp4box frees a slice only once all of its bytes were used and a
     * video's bytes never are (+677 MB for a 572 MB clip, tree working set).
     *     sinkChannels  how many planes the sink wants (default: all); the
     *                   planes are scratch, valid during the call only
     *     onInfo({trackSec, movieSec, channels, sampleRate})  once, before the feed
     *     onProgress(fraction)  per read;  signal  an AbortSignal
     *
     * Rejects (typed by message) on: no AudioDecoder, not mp4-family, no moov,
     * no audio track, unsupported codec, decoder error, 60 s of no progress.
     * The caller's ladder decides what to try next — see
     * AudioDecoderRobust.robustRange.
     */
    var RANGE_MOOV_CAP = 64 * 1048576;       // an index bigger than this is not a media file's index
    var RANGE_RUN_BYTES = 8 * 1048576;       // one disk read is at most this
    var RANGE_RUN_GAP = 4096;                // units closer than this are read in one run: the same stsc chunk, not the video between chunks
    var RANGE_WARMUP_UNITS = 2;              // AAC overlap-add: the frame before the cut must be decoded too
    var RANGE_SEEK_BUDGET = 2000;            // reads under the gap rule past which reading straight through wins (readThrough)

    /* FFmpeg-made mp4 (and anything that went through an editor or a remux)
       puts every AAC unit in a chunk of its own between two video frames, so
       the gap rule below pays one disk read per unit: 75 400 reads for the
       sound of a 27-min 1080p120 file. Past a read budget — and only when the
       windows are far fewer — the feed reads straight through in
       RANGE_RUN_BYTES windows: the video bytes in between are read and
       dropped, the heap still holds one window. A phone file (a second of
       audio per chunk) stays under the budget and reads audio bytes only.
       window.__wcRangeReads = 'gap' | 'through' forces a strategy (stands). */
    function readThrough(si, i0, i1) {
        var force = window.__wcRangeReads;
        if (force === 'gap') return false;
        if (force === 'through') return true;
        var runs = 0, hi = -1;
        for (var i = i0; i < i1; i++) {
            if (!(si[i].offset >= hi && si[i].offset - hi <= RANGE_RUN_GAP)) runs++;
            hi = si[i].offset + si[i].size;
        }
        if (runs <= RANGE_SEEK_BUDGET) return false;
        var span = si[i1 - 1].offset + si[i1 - 1].size - si[i0].offset;
        return span / RANGE_RUN_BYTES * 4 < runs;
    }

    async function topLevelBoxes(file){
        var boxes = [], off = 0, guard = 0;
        while (off < file.size && guard++ < 4096) {
            var h = new DataView(await file.slice(off, off + 16).arrayBuffer());
            if (h.byteLength < 8) break;
            var size = h.getUint32(0), hdr = 8;
            var type = String.fromCharCode(h.getUint8(4), h.getUint8(5), h.getUint8(6), h.getUint8(7));
            if (size === 1) {
                if (h.byteLength < 16) break;
                size = h.getUint32(8) * 4294967296 + h.getUint32(12); hdr = 16;
            } else if (size === 0) {
                size = file.size - off;
            }
            if (!(size >= hdr) || off + size > file.size) break;
            boxes.push({ type: type, start: off, size: size });
            off += size;
        }
        return boxes;
    }

    async function decodeRange(file, ctx, opts){
        opts = opts || {};
        if (typeof AudioDecoder === 'undefined' || typeof EncodedAudioChunk === 'undefined') throw new Error('no AudioDecoder');
        var sink = typeof opts.sink === 'function' ? opts.sink : null;
        if (!ctx && !sink) throw new Error('no AudioContext');
        if (!file || !isMp4Family(file)) throw new Error('webcodecs range: not an mp4-family file');
        var signal = opts.signal || null;
        var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
        var from = Math.max(0, +opts.from || 0);
        var to = (opts.to == null || !isFinite(+opts.to)) ? Infinity : Math.max(from, +opts.to);
        var t0 = Date.now();
        await loadMp4Box();

        // ftyp + moov, back to back, wherever the moov sits in the file — the
        // same synthetic stream video-merge-webcodecs demuxLazy feeds MP4Box.
        var boxes = await topLevelBoxes(file), ftyp = null, moov = null;
        for (var b = 0; b < boxes.length; b++) {
            if (boxes[b].type === 'ftyp' && !ftyp) ftyp = boxes[b];
            if (boxes[b].type === 'moov' && !moov) moov = boxes[b];
        }
        if (!moov) throw new Error('webcodecs range: no moov');
        if (moov.size > RANGE_MOOV_CAP) throw new Error('webcodecs range: moov too large');
        var wanted = ftyp ? [ftyp, moov] : [moov], parts = [], total = 0;
        for (var w = 0; w < wanted.length; w++) {
            var u = new Uint8Array(await file.slice(wanted[w].start, wanted[w].start + wanted[w].size).arrayBuffer());
            parts.push(u); total += u.length;
        }
        var head = new Uint8Array(total), at0 = 0;
        for (var p = 0; p < parts.length; p++) { head.set(parts[p], at0); at0 += parts[p].length; }

        var mp4 = MP4Box.createFile();
        var info = await new Promise(function(resolve, reject){
            var settled = false;
            mp4.onError = function(e){ if (!settled) { settled = true; reject(new Error('mp4box: ' + e)); } };
            mp4.onReady = function(inf){ if (!settled) { settled = true; resolve(inf); } };
            try {
                var ab = head.buffer.slice(0); ab.fileStart = 0;
                mp4.appendBuffer(ab); mp4.flush();
            } catch (e) { if (!settled) { settled = true; reject(e); } }
            // A verdict, as in decode(): MP4Box fires onReady from inside the
            // append or never.
            if (!settled) { settled = true; reject(new Error('webcodecs range: no mp4 track header')); }
        });
        var at = info.audioTracks && info.audioTracks[0];
        if (!at) {
            // A window of G.711 is not read here (the whole-file decode() does
            // it); the ladder's next rung takes the file — but by its real name.
            var snd = unreadSoundTrack(mp4, info);
            throw new Error(snd ? 'no decoder for sound entry ' + snd.type : 'no audio track');
        }
        var hdrRate = at.audio.sample_rate, hdrChannels = at.audio.channel_count;
        var trackDurSec = (at.duration > 0 && at.timescale > 0) ? at.duration / at.timescale : 0;

        // The edit list — head cut in seconds of MEDIA time, exactly as decode().
        var editSkipSec = 0;
        try {
            var trak = mp4.getTrackById(at.id);
            var ents = (at.edits && at.edits.length) ? at.edits
                : (trak && trak.edts && trak.edts.elst && trak.edts.elst.entries);
            var mvTs = mp4.moov && mp4.moov.mvhd && mp4.moov.mvhd.timescale;
            var ML = window.Mp4EditList;
            var pl = (ML && typeof ML.plan === 'function') ? ML.plan(at, mvTs) : null;
            if (pl && (pl.shape === 'media' || pl.shape === 'empty+media')) editSkipSec = pl.mediaStartUs / 1e6;
            else if (ents && ents.length === 1 && ents[0].media_time > 0 && at.timescale > 0) editSkipSec = ents[0].media_time / at.timescale;
        } catch (_) {}

        var si = mp4.getTrackSamplesInfo(at.id);
        if (!si || !si.length) throw new Error('webcodecs range: no audio samples');
        var mFrom = from + editSkipSec;
        var mTo = to === Infinity ? Infinity : to + editSkipSec;
        // Units overlapping [mFrom, mTo) in media time, plus the warm-up before.
        var i0 = 0, i1 = si.length;
        for (var i = 0; i < si.length; i++) {
            var s = si[i], ts = s.cts / s.timescale, te = ts + (s.duration || 0) / s.timescale;
            if (te <= mFrom) i0 = i + 1;
            if (ts >= mTo) { i1 = i; break; }
        }
        i0 = Math.max(0, i0 - RANGE_WARMUP_UNITS);
        if (i1 <= i0) i1 = Math.min(si.length, i0 + 1);   // a window past the end: decode one unit, get silence

        var codec = at.codec; if (codec.indexOf('.') < 0 && codec.indexOf('mp4a') === 0) codec = 'mp4a.40.2';
        var realAsc = descFromEsds(mp4, at.id);
        var desc = realAsc || aacAsc(hdrRate, hdrChannels);
        var coreRate = (realAsc && ascCoreRate(realAsc)) || hdrRate;
        var cands = [
            { codec: codec, sampleRate: coreRate, numberOfChannels: hdrChannels, description: desc },
            { codec: codec, sampleRate: hdrRate, numberOfChannels: hdrChannels, description: desc },
            { codec: codec, sampleRate: hdrRate, numberOfChannels: hdrChannels },
        ];
        var useCfg = null;
        for (var c = 0; c < cands.length && !useCfg; c++) {
            try { var sup = await AudioDecoder.isConfigSupported(cands[c]); if (sup && sup.supported) useCfg = cands[c]; } catch (_) {}
        }
        if (!useCfg) throw new Error('config unsupported: ' + codec);
        if (sink && typeof opts.onInfo === 'function') {
            opts.onInfo({
                trackSec: Math.max(0, trackDurSec - editSkipSec),
                movieSec: (info.duration > 0 && info.timescale > 0) ? info.duration / info.timescale : 0,
                channels: hdrChannels, sampleRate: hdrRate
            });
        }
        var sinkCh = sink ? Math.max(1, opts.sinkChannels | 0 || 64) : 0;

        return await new Promise(function(resolve, reject){
            var settled = false, decErr = null, dec = null;
            var segs = [];                    // { ts (µs, media), planes: Float32Array[] , frames }
            var outChannels = 0, labelRate = 0, framesPerChunk = [], chunkSeconds = [];
            var reads = 0, bytes = 0, fed = 0;
            var WATCHDOG_MS = (window.__wcStallMs | 0) || 60000, watchdog = null;
            var scratch = [];
            function fail(e){ if (settled) return; settled = true; clearTimeout(watchdog); try { if (dec && dec.state !== 'closed') dec.close(); } catch (_) {} reject(e && e.message ? e : new Error('webcodecs range error')); }
            function arm(){ if (settled) return; clearTimeout(watchdog); watchdog = setTimeout(function(){ fail(new Error('webcodecs range timeout')); }, WATCHDOG_MS); }
            arm();
            dec = new AudioDecoder({
                output: function(adt){
                    arm();
                    var nf = adt.numberOfFrames, nch = adt.numberOfChannels;
                    if (!outChannels) { outChannels = nch; labelRate = adt.sampleRate; }
                    else if (nch !== outChannels) { decErr = new Error('webcodecs range: channel count changed mid-stream'); adt.close(); return; }
                    if (framesPerChunk.length < 256) framesPerChunk.push(nf);
                    if (sink) {
                        // [from, to) on the decoder's own clock; the frame is handed on and dropped
                        var sr = adt.sampleRate || labelRate;
                        var st = Math.round((adt.timestamp / 1e6 - mFrom) * sr);
                        var a = st < 0 ? Math.min(nf, -st) : 0;
                        var z = mTo === Infinity ? nf : Math.max(a, Math.min(nf, Math.round((mTo - mFrom) * sr) - st));
                        if (z > a && !decErr) {
                            var sp = [];
                            for (var sc = 0; sc < Math.min(nch, sinkCh); sc++) {
                                var so = scratch[sc];
                                if (!so || so.length < nf) so = scratch[sc] = new Float32Array(Math.max(nf, 4096));
                                try { adt.copyTo(so, { planeIndex: sc, format: 'f32-planar' }); } catch (e) { decErr = e; }
                                sp.push(so.subarray(a, z));
                            }
                            try { sink(sp, sr); } catch (e) { decErr = e; }
                        }
                        adt.close();
                        return;
                    }
                    var planes = [];
                    for (var ch = 0; ch < nch; ch++) {
                        var out = new Float32Array(nf);
                        try { adt.copyTo(out, { planeIndex: ch, format: 'f32-planar' }); } catch (e) { decErr = e; }
                        planes.push(out);
                    }
                    segs.push({ ts: adt.timestamp, frames: nf, planes: planes });
                    adt.close();
                },
                error: function(e){ decErr = e; }
            });
            try { dec.configure(useCfg); } catch (e) { fail(e); return; }

            (async function feed(){
                // Runs of near-contiguous units → one slice each. An interleaved
                // phone file keeps each chunk's audio units back to back, so a run
                // is usually one stsc chunk: only audio bytes leave the disk.
                var through = readThrough(si, i0, i1);
                var i = i0;
                while (i < i1) {
                    if (signal && signal.aborted) { var ab0 = new Error('webcodecs range: aborted'); ab0.name = 'AbortError'; throw ab0; }
                    var lo = si[i].offset, hi = lo + si[i].size, j = i + 1;
                    while (j < i1 && si[j].offset >= hi && (through || si[j].offset - hi <= RANGE_RUN_GAP) && (si[j].offset + si[j].size - lo) <= RANGE_RUN_BYTES) {
                        hi = si[j].offset + si[j].size; j++;
                    }
                    var run = new Uint8Array(await file.slice(lo, hi).arrayBuffer());
                    reads++; bytes += run.length;
                    for (var k = i; k < j; k++) {
                        var x = si[k];
                        if (x.duration > 0 && x.timescale > 0 && chunkSeconds.length < 256) {
                            var cs = x.duration / x.timescale;
                            if (cs > 0.005 && cs < 0.5) chunkSeconds.push(cs);
                        }
                        if (decErr) throw decErr;
                        dec.decode(new EncodedAudioChunk({
                            type: 'key',
                            timestamp: Math.round(x.cts / x.timescale * 1e6),
                            duration: Math.round((x.duration || 0) / x.timescale * 1e6),
                            data: run.subarray(x.offset - lo, x.offset - lo + x.size)
                        }));
                        fed++;
                        while (dec.decodeQueueSize > 32) { await new Promise(function(r){ setTimeout(r, 0); }); if (decErr) throw decErr; }
                    }
                    i = j;
                    if (onProgress) { try { onProgress((i - i0) / (i1 - i0)); } catch (_) {} }
                }
                await dec.flush();
                if (decErr) throw decErr;
                if (settled) return;
                settled = true; clearTimeout(watchdog);
                try { dec.close(); } catch (_) {}
                if (sink) {
                    if (!outChannels) { reject(new Error('webcodecs range decoded 0 frames')); return; }
                    resolve({ rate: measureRate(framesPerChunk, chunkSeconds, labelRate) || labelRate, channels: outChannels,
                        units: fed, reads: reads, bytes: bytes, through: through ? 1 : 0, ms: Date.now() - t0, editSkipSec: editSkipSec });
                    return;
                }
                if (!segs.length) { reject(new Error('webcodecs range decoded 0 frames')); return; }
                var rate = measureRate(framesPerChunk, chunkSeconds, labelRate);
                if (!(rate > 0)) { reject(new Error('webcodecs range: no sample rate')); return; }
                // The SLOT is what the caller asked for: a finite `to` past the
                // track end comes back as silence there. Open-ended = the track.
                var endSec = to;
                if (to === Infinity) {
                    var last = si[si.length - 1];
                    var trackEnd = (last.cts + (last.duration || 0)) / last.timescale - editSkipSec;
                    endSec = Math.max(from, trackDurSec > 0 ? Math.min(trackEnd, trackDurSec) : trackEnd);
                }
                var len = Math.max(1, Math.round((endSec - from) * rate));
                var ab = ctx.createBuffer(outChannels || hdrChannels || 1, len, rate);
                for (var q = 0; q < segs.length; q++) {
                    var sg = segs[q];
                    var start = Math.round((sg.ts / 1e6 - mFrom) * rate);   // position inside the window
                    var sFrom = start < 0 ? -start : 0;
                    var sTo = Math.min(sg.frames, len - start);
                    if (sTo <= sFrom) continue;
                    for (var ch2 = 0; ch2 < ab.numberOfChannels; ch2++) {
                        var pl2 = sg.planes[Math.min(ch2, sg.planes.length - 1)];
                        ab.getChannelData(ch2).set(pl2.subarray(sFrom, sTo), start + sFrom);
                    }
                }
                ab._range = { from: from, to: endSec, units: fed, reads: reads, bytes: bytes, through: through ? 1 : 0, ms: Date.now() - t0, rate: rate, editSkipSec: editSkipSec };
                resolve(ab);
            })().catch(fail);
        });
    }

    window.WebCodecsAudioDecode = {
        supported: supported,
        prefersFirst: prefersFirst,
        nativeAfterFailure: nativeAfterFailure,
        decode: decode,
        decodeRange: decodeRange,
        isMp4Family: isMp4Family,
        // Pure helpers, exposed for the node guard.
        // For relays that decode → re-encode unit by unit (see relayFormat).
        relayFormat: relayFormat,
        conformAudioData: conformAudioData,
        _measureRate: measureRate,
        _snapRate: snapRate,
        _ascCoreRate: ascCoreRate,
        _readThrough: readThrough
    };
})();
